import React, { useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Modal, Field, Area, Kicker } from "@/components/kit";
import { ROMAN, relTime } from "@/lib/util";

const domain = (url) => {
  try { return new URL(url).hostname.replace("www.", ""); } catch { return "link"; }
};

export default function Library() {
  const { resources, meetings, memberById, yearId, refresh, canWrite, me } = useTable();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", notes: "", meetingId: "" });
  const [filter, setFilter] = useState("All");

  const domains = ["All", ...Array.from(new Set(resources.map((r) => domain(r.url))))];
  const shown = resources.filter((r) => filter === "All" || domain(r.url) === filter);

  const add = async () => {
    try {
      await api.post("/resources", { ...form, yearId });
      setOpen(false); setForm({ title: "", url: "", notes: "", meetingId: "" });
      await refresh();
      toast.success("Added to the library.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="library-page">
      <PageHead
        kicker="Kept for later"
        title="Library"
        sub="What the room recommended — books, papers, tools, talks."
        right={canWrite && <button className="btn-ink" data-testid="add-resource" onClick={() => setOpen(true)}>Add</button>}
      />

      {domains.length > 2 && (
        <div className="flex gap-2 flex-wrap mb-5">
          {domains.map((d) => (
            <button
              key={d}
              className="chip"
              data-testid={`library-filter-${d}`}
              onClick={() => setFilter(d)}
              style={filter === d ? { background: "var(--ink-panel)", color: "#FFFFFF", borderColor: "var(--ink-panel)" } : {}}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <Empty line="Empty shelves. The first recommendation starts it." testId="library-empty" />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {shown.map((r) => {
            const ch = meetings.find((m) => m.id === r.meetingId);
            const by = memberById(r.addedById);
            return (
              <Card key={r.id} data-testid={`resource-${r.id}`}>
                <h3 className="serif text-xl leading-snug">{r.title}</h3>
                {r.notes && <p className="text-sm mt-2" style={{ color: "var(--ink-mute)" }}>{r.notes}</p>}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Chip>{domain(r.url)}</Chip>
                  {ch && <Chip>Chapter {ROMAN[ch.num - 1]}</Chip>}
                  <Chip>{by?.name || "—"}</Chip>
                </div>
                <a href={r.url} target="_blank" rel="noreferrer" className="btn-quiet inline-block mt-4">Open</a>
                {(me.isAdmin || r.addedById === me.memberId) && canWrite && (
                  <button
                    className="btn-quiet mt-4 ml-2"
                    data-testid={`resource-delete-${r.id}`}
                    onClick={async () => { await api.delete(`/resources/${r.id}`); await refresh(); }}
                  >
                    Remove
                  </button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add a resource" testId="resource-modal">
        <Field label="Title" value={form.title} data-testid="resource-title" onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field label="URL" value={form.url} data-testid="resource-url" onChange={(e) => setForm({ ...form, url: e.target.value })} />
        <Area label="Why it matters" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <label className="block">
          <span className="kicker block mb-1.5">Chapter (optional)</span>
          <select className="field" value={form.meetingId} onChange={(e) => setForm({ ...form, meetingId: e.target.value })}>
            <option value="">None</option>
            {meetings.map((m) => <option key={m.id} value={m.id}>Chapter {ROMAN[m.num - 1]}</option>)}
          </select>
        </label>
        <button className="btn-ink w-full" data-testid="resource-save" onClick={add}>Add</button>
      </Modal>
    </div>
  );
}
